import { useCallback, useEffect, useRef, useState } from 'react';
import { useDismissable } from '../../hooks';

/**
 * The page search bar, with the answers under it as you type.
 *
 * Distinct from `SearchSelect`, which is a form FIELD that filters a list it
 * was handed. This is a page's own search box over a universe too large to
 * hand anybody: the scheme master is ~1,500 rows and the equity master ten
 * times that, so the matching happens on the server and the caller passes the
 * results back in. What that costs is a round trip per keystroke, which is why
 * every caller debounces what it sends (see `useDebounced`) — this component
 * is told what to show, never when to fetch.
 *
 * Two ways out, because a search box here answers two different questions:
 *
 *   Enter on a highlighted row → `onPick`, which is "take me to this one".
 *   Enter on nothing           → `onSubmit`, which is "filter the page by what
 *                                I typed", the behaviour the plain box had.
 *
 * A caller with no page-level filter simply omits `onSubmit`, and Enter then
 * takes the first row — the ordinary lookup.
 */

/** Typing below this is not a search, it is the first letter of one. */
const MIN_CHARS = 2;

export default function Lookup({
  id = 'lookup',
  value,
  onChange,
  results = [],
  loading = false,
  placeholder,
  onPick,
  onSubmit,
  onClear,
  minChars = MIN_CHARS,
  emptyLabel = 'No matches',
  /* Said under the rows: what Enter would do with what is typed, so the
     page-wide filter is discoverable rather than a thing you find by accident. */
  submitLabel,
  hint,
}) {
  const [focused, setFocused] = useState(false);
  const [active, setActive] = useState(0);
  /* Whether the reader has MOVED onto a row, as opposed to the first row
     simply being the one highlighted. It decides what Enter means on a box
     that also filters the page — see `keyDown`. Not `byKeyboard`, which the
     scroll effect consumes on the very next render. */
  const [moved, setMoved] = useState(false);
  /* A pick or a submit closes the list, and it must STAY closed while the box
     still holds the text that opened it — otherwise dismissing it and leaving
     the words in the box reopens the dropdown on the next render. */
  const [dismissed, setDismissed] = useState(false);
  const box = useRef(null);
  const list = useRef(null);
  const byKeyboard = useRef(false);

  const typed = (value ?? '').trim();
  const open = focused && !dismissed && typed.length >= minChars;

  const close = useCallback(() => { setDismissed(true); }, []);
  useDismissable(open, box, close);

  /* Typing again is always a new search, so it reopens what a pick closed. */
  const change = (next) => {
    setDismissed(false);
    setActive(0);
    setMoved(false);
    onChange(next);
  };

  /* A new set of answers is read from the top. Keyed on the symbols rather
     than on the array, because a caller that builds its rows in render hands
     us a new array every time — and resetting the highlight on every render
     would make the arrow keys unusable. */
  const resultsKey = results.map((r) => r.value).join('|');
  useEffect(() => { setActive(0); setMoved(false); if (list.current) list.current.scrollTop = 0; }, [resultsKey]);

  /**
   * Keep the highlighted row in view, scrolling the LIST and nothing else.
   *
   * `scrollIntoView` scrolls every scrollable ancestor including the page, and
   * a page that moves under a stationary cursor drags a different row beneath
   * it, which fires `mouseenter`, which moves the highlight, which scrolls
   * again. Same runaway `SearchSelect` had, same fix: only on a key press.
   */
  useEffect(() => {
    if (!open || !byKeyboard.current) return;
    byKeyboard.current = false;
    const ul = list.current;
    const li = ul?.querySelector(`[data-i="${active}"]`);
    if (!ul || !li) return;
    if (li.offsetTop < ul.scrollTop) ul.scrollTop = li.offsetTop;
    else if (li.offsetTop + li.offsetHeight > ul.scrollTop + ul.clientHeight) {
      ul.scrollTop = li.offsetTop + li.offsetHeight - ul.clientHeight;
    }
  }, [open, active]);

  const pick = (row) => {
    setDismissed(true);
    onPick?.(row);
  };

  const keyDown = (e) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      if (!results.length) return;
      e.preventDefault();
      setDismissed(false);
      setMoved(true);
      byKeyboard.current = true;
      const step = e.key === 'ArrowDown' ? 1 : -1;
      setActive((i) => Math.min(results.length - 1, Math.max(0, i + step)));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      /* The highlighted row wins, except that a caller WITH a page filter only
         has a row highlighted once the reader has moved onto it — Enter
         straight after typing filters the page, which is what the box has
         always done and what muscle memory expects. */
      const row = open && results[active] ? results[active] : null;
      if (row && (moved || !onSubmit)) pick(row);
      else if (onSubmit) { setDismissed(true); onSubmit(typed); }
      else if (row) pick(row);
    } else if (e.key === 'Escape') {
      if (open) { e.preventDefault(); close(); }
    }
  };

  return (
    <div ref={box} style={{ position: 'relative' }}>
      <div
        className="search"
        style={{ maxWidth: '100%', background: 'var(--panel)', padding: '11px 14px' }}
      >
        <span aria-hidden="true">⌕</span>
        <input
          role="combobox"
          aria-expanded={open}
          aria-controls={`${id}-list`}
          aria-autocomplete="list"
          autoComplete="off"
          placeholder={placeholder}
          value={value ?? ''}
          onChange={(e) => change(e.target.value)}
          onFocus={() => { setFocused(true); setDismissed(false); }}
          onBlur={() => {
            /* Not closed here: a press on a row blurs the input before the
               click lands, and closing on blur would unmount the row first.
               `useDismissable` handles the press outside. */
            setFocused(true);
          }}
          onKeyDown={keyDown}
        />
        {/* A search in force is cleared from where it is shown, not by
            selecting the text and deleting it. */}
        {typed && onClear ? (
          <span
            className="link"
            style={{ cursor: 'pointer', fontSize: 11.5 }}
            onClick={() => { setDismissed(true); onClear(); }}
          >
            Clear
          </span>
        ) : (
          <span className="kbd">↵</span>
        )}
      </div>

      {open && (
        <ul
          id={`${id}-list`}
          ref={list}
          role="listbox"
          style={{
            position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 40,
            maxHeight: 340, overflowY: 'auto', listStyle: 'none', padding: 4, margin: '6px 0 0',
            background: 'var(--panel)', border: '1px solid var(--line)', borderRadius: 10,
            boxShadow: '0 12px 32px rgba(0,0,0,.18)',
          }}
        >
          {/* Said above the rows rather than under the box, because it is about
              what is IN the list — "these are guesses, not matches". */}
          {hint && (
            <li style={{ padding: '7px 10px 6px', fontSize: 10.8, color: 'var(--ink3)' }}>{hint}</li>
          )}

          {results.map((r, i) => (
            <li
              key={r.value}
              data-i={i}
              role="option"
              aria-selected={i === active}
              onMouseEnter={() => { setActive(i); setMoved(true); }}
              onClick={() => pick(r)}
              style={{
                padding: '8px 10px', borderRadius: 7, cursor: 'pointer',
                display: 'flex', alignItems: 'baseline', gap: 10,
                background: i === active ? 'color-mix(in srgb,var(--brand) 12%,transparent)' : 'transparent',
              }}
            >
              <span style={{ flex: 1, minWidth: 0, fontSize: 13 }}>
                <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {r.label}
                </span>
                {r.meta && (
                  <span style={{ display: 'block', fontSize: 11, color: 'var(--ink3)', marginTop: 1 }}>
                    {r.meta}
                  </span>
                )}
              </span>
              {r.tag && (
                <span style={{ fontSize: 10.5, color: 'var(--ink3)', whiteSpace: 'nowrap' }}>{r.tag}</span>
              )}
            </li>
          ))}

          {/* Loading and empty are different states and are never shown as the
              same thing: "No matches" under a query that is still in flight is
              simply wrong, and it is the one moment a reader gives up. */}
          {!results.length && (
            <li style={{ padding: '9px 10px', fontSize: 12.5, color: 'var(--ink3)' }}>
              {loading ? 'Searching…' : emptyLabel}
            </li>
          )}
          {loading && results.length > 0 && (
            <li style={{ padding: '6px 10px', fontSize: 11, color: 'var(--ink3)' }}>Searching…</li>
          )}

          {submitLabel && onSubmit && (
            <li
              onClick={() => { setDismissed(true); onSubmit(typed); }}
              style={{
                padding: '8px 10px', marginTop: 2, borderTop: '1px solid var(--line2)',
                fontSize: 11.5, color: 'var(--ink3)', cursor: 'pointer',
              }}
            >
              {submitLabel} <span className="kbd" style={{ marginLeft: 4 }}>↵</span>
            </li>
          )}
        </ul>
      )}


    </div>
  );
}
